import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Info } from 'lucide-react';
import { ImmunizationCatalogItem, ImmunizationCatalogItemCreate } from '../../types/immunization-catalog';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface ImmunizationCatalogItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ImmunizationCatalogItemCreate) => Promise<void>;
  onHardDelete?: (itemId: string) => Promise<void>;
  item?: ImmunizationCatalogItem | null;
  mode: 'create' | 'edit';
}

interface ReferenceInfo {
  scheduled_vaccines?: number;
  template_items?: number;
}

const ImmunizationCatalogItemModal: React.FC<ImmunizationCatalogItemModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onHardDelete,
  item,
  mode
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<ImmunizationCatalogItemCreate>({
    name: '',
    default_site: '',
    default_manufacturer: '',
    typical_duration_days: undefined,
    notes: '',
    display_order: 0,
    // PIMS-level fields
    initial_duration_days: undefined,
    booster_duration_days: undefined,
    product_type: undefined,
    canonical_name: undefined,
    species: undefined,
    is_stock_tracked: false,
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [references, setReferences] = useState<ReferenceInfo | null>(null);
  const [loadingRefs, setLoadingRefs] = useState(false);

  // Can only hard delete custom (non-standard) items in edit mode
  const canHardDelete = mode === 'edit' && item && !item.is_standard && onHardDelete;
  const hasReferences = references && (references.scheduled_vaccines || references.template_items);

  const handleHardDelete = async () => {
    if (!item || !onHardDelete) return;
    
    setDeleting(true);
    setError(null);
    
    try {
      await onHardDelete(item.id);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete item');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  // Check references when modal opens for custom items
  useEffect(() => {
    const checkReferences = async () => {
      if (!isOpen || !canHardDelete || !item || !user?.practice_id) {
        setReferences(null);
        return;
      }
      
      setLoadingRefs(true);
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(
          `${API_ENDPOINTS.IMMUNIZATION_CATALOG.LIST(user.practice_id)}/${item.id}/references`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const refs = await response.json();
          setReferences(refs);
        }
      } catch {
        // Silently fail - we'll just show the delete button
        setReferences(null);
      } finally {
        setLoadingRefs(false);
      }
    };
    
    checkReferences();
  }, [isOpen, canHardDelete, item?.id, user?.practice_id]);

  useEffect(() => {
    if (item) {
      // For both edit and create mode (create mode when overriding a standard item)
      setFormData({
        name: item.name,
        default_site: item.default_site || '',
        default_manufacturer: item.default_manufacturer || '',
        typical_duration_days: item.typical_duration_days,
        notes: item.notes || '',
        display_order: item.display_order,
        // PIMS-level fields
        initial_duration_days: item.initial_duration_days,
        booster_duration_days: item.booster_duration_days,
        product_type: item.product_type,
        canonical_name: item.canonical_name,
        species: item.species,
        is_stock_tracked: item.is_stock_tracked ?? false,
      });
    } else {
      // Reset form for create mode with no item
      setFormData({
        name: '',
        default_site: '',
        default_manufacturer: '',
        typical_duration_days: undefined,
        notes: '',
        display_order: 0,
        initial_duration_days: undefined,
        booster_duration_days: undefined,
        product_type: undefined,
        canonical_name: undefined,
        species: undefined,
        is_stock_tracked: false,
      });
    }
    setError(null);
    setShowDeleteConfirm(false);
  }, [item, mode, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      await onSave(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'create' ? (item?.is_standard ? 'Override Standard Immunization' : 'Add Custom Immunization') : 'Edit Immunization'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              {error}
            </div>
          )}

          {item?.is_standard && mode === 'create' && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-700">
              You're creating a practice-specific version of "{item.name}". This will override the standard item for your practice.
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_stock_tracked"
                checked={!!formData.is_stock_tracked}
                onChange={(e) => setFormData({ ...formData, is_stock_tracked: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_stock_tracked" className="text-sm font-medium text-gray-700">
                Track as Inventory (stock-tracked)
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Immunization Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Rabies, DHPP, FVRCP"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Site
                </label>
                <input
                  type="text"
                  value={formData.default_site || ''}
                  onChange={(e) => setFormData({ ...formData, default_site: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., RF, LH, SQ, IM"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Typical Duration (days)
                </label>
                <input
                  type="number"
                  value={formData.typical_duration_days || ''}
                  onChange={(e) => setFormData({ ...formData, typical_duration_days: e.target.value ? parseInt(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="365"
                  min="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Manufacturer
              </label>
              <input
                type="text"
                value={formData.default_manufacturer || ''}
                onChange={(e) => setFormData({ ...formData, default_manufacturer: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Zoetis, Merck, Boehringer"
              />
            </div>

            {/* PIMS-Level Scheduling Fields */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Advanced Scheduling (PIMS)</h3>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Initial Duration (days)
                  </label>
                  <input
                    type="number"
                    value={formData.initial_duration_days || ''}
                    onChange={(e) => setFormData({ ...formData, initial_duration_days: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="365"
                    min="0"
                  />
                  <p className="mt-1 text-xs text-gray-500">Days until first booster</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Booster Duration (days)
                  </label>
                  <input
                    type="number"
                    value={formData.booster_duration_days || ''}
                    onChange={(e) => setFormData({ ...formData, booster_duration_days: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1095"
                    min="0"
                  />
                  <p className="mt-1 text-xs text-gray-500">Days for subsequent boosters</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Type
                  </label>
                  <select
                    value={formData.product_type || ''}
                    onChange={(e) => setFormData({ ...formData, product_type: e.target.value || undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select type...</option>
                    <option value="1-year">1-Year</option>
                    <option value="3-year">3-Year</option>
                    <option value="core">Core</option>
                    <option value="non-core">Non-Core</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Species
                  </label>
                  <select
                    value={formData.species || ''}
                    onChange={(e) => setFormData({ ...formData, species: e.target.value || undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All species</option>
                    <option value="canine">Canine</option>
                    <option value="feline">Feline</option>
                    <option value="all">All</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Canonical Name
                </label>
                <input
                  type="text"
                  value={formData.canonical_name || ''}
                  onChange={(e) => setFormData({ ...formData, canonical_name: e.target.value.toUpperCase() || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., DHPP, RABIES, FVRCP"
                />
                <p className="mt-1 text-xs text-gray-500">Normalized name for matching (auto-converts to uppercase)</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Additional notes or instructions"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Order
              </label>
              <input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
              />
            </div>

            {/* Delete Section - only for custom items in edit mode */}
            {canHardDelete && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                {loadingRefs ? (
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-gray-600"></div>
                    <span className="text-sm">Checking usage...</span>
                  </div>
                ) : hasReferences ? (
                  /* Item is in use - show info, no delete option */
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                          This item is currently in use
                        </h3>
                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                          {references?.scheduled_vaccines && `${references.scheduled_vaccines} scheduled vaccine${references.scheduled_vaccines > 1 ? 's' : ''}`}
                          {references?.scheduled_vaccines && references?.template_items && ' and '}
                          {references?.template_items && `${references.template_items} template${references.template_items > 1 ? 's' : ''}`}
                          {' '}reference this item.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* No references - show delete option */
                  <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Delete this item
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                          Permanently remove this custom immunization
                        </p>
                      </div>
                      
                      {!showDeleteConfirm ? (
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(true)}
                          className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          Delete
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handleHardDelete}
                            disabled={deleting}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                          >
                            {deleting ? 'Deleting...' : 'Confirm'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowDeleteConfirm(false)}
                            disabled={deleting}
                            className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {error && (
                      <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
                        {error}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImmunizationCatalogItemModal;



